import Image from "next/image";

import { useFunnelContext } from "shared/Funnel/FunnelContextProvider";
import { useSettings } from "shared/SettingsProvider/useSettings";
import { useProductPageContext } from "shared/product/ProductPageContext/useProductPageContext";
import { Loader } from "ui/Loader";
import { PriceDropBadge } from "ui/PriceDropBadge";
import { formatToCurrency, priceAfterDiscount } from "utils/price.utils";
import { usePriceAdvantage } from "utils/usePriceAdvantage";
import { useI18n } from "utils/with-i18n.utils";

import DropArrow from "./drop-arrow.svg";

export const ProductPriceDetails = () => {
  const i18n = useI18n();
  const { currentVariant, subscriptionDropSkus } = useProductPageContext();
  const { isBlackFriday } = useSettings();
  const { isSwap } = useFunnelContext();

  const priceAdvantage = usePriceAdvantage({
    originalUpfrontPrice: currentVariant?.originalUpfrontPrice,
    upfrontPrice: currentVariant?.upfrontPrice,
    originalSubscriptionPrice: currentVariant?.originalSubscriptionPrice,
    subscriptionPrice: currentVariant?.subscriptionPrice,
  });

  const {
    discountInfo,
    formatedOriginalPriceUpfront,
    formatedOriginalPriceSubscription,
    discountPercentage,
    hasUpfrontPriceDrop,
    currentSeniorityPlan,
    hasSeniorityDiscount,
    smartphoneFamilyHasSubscriptionPriceDrop,
  } = priceAdvantage;

  if (isSwap && !discountInfo) {
    return <Loader className="my-8 mx-auto" />;
  }

  const upfrontPriceLabel =
    hasUpfrontPriceDrop || currentSeniorityPlan
      ? i18n.t(
          "product.available.upfrontDiscount",
          {
            originalPrice: formatedOriginalPriceUpfront,
            price: formatToCurrency(
              "fr-FR",
              "EUR",
              priceAfterDiscount(
                currentVariant?.upfrontPrice ?? 0,
                discountPercentage ?? 0,
              ),
            ).replace(",00", ""),
            originalPriceClassName: "font-semibold text-gray-4",
            priceClassName: "font-semibold text-cornflower-blue",
            labelClassName: "text-sm lg:text-base",
          },
          { html: true },
        )
      : i18n.t(
          "product.available.upfront",
          {
            price: formatedOriginalPriceUpfront,
            className: "font-semibold",
          },
          { html: true },
        );

  // check if the sku of the currentVariant is part of the subscriptionDropSkus
  // i.e., currentVariant's sku has formerSubscriptionPriceInCentsIncludingTax
  const currentVariantSkuHasPromotion =
    smartphoneFamilyHasSubscriptionPriceDrop &&
    subscriptionDropSkus?.includes(currentVariant?.sku as string);

  const subscriptionPriceLabelOptions = currentVariantSkuHasPromotion
    ? {
        originalPrice: formatedOriginalPriceSubscription,
        price: formatToCurrency(
          "fr-FR",
          "EUR",
          currentVariant?.subscriptionPrice ?? 0,
          undefined,
          false,
        ),
        originalPriceClassName:
          "font-semibold mr-2 text-2xl lg:text-3xl text-gray-4",
        priceClassName:
          "font-bold text-2xl lg:text-3xl text-cornflower-blue bg-yellow",
      }
    : {
        price: formatToCurrency(
          "fr-FR",
          "EUR",
          currentVariant?.subscriptionPrice ?? 0,
        ),
        priceClassName: "font-bold bg-yellow text-2xl lg:text-3xl",
      };

  const subscriptionPriceLabel = i18n.t(
    currentVariantSkuHasPromotion
      ? "product.available.subscriptionDiscount"
      : "product.available.subscription",
    subscriptionPriceLabelOptions,
    { html: true },
  );

  const isBlackWeekProduct =
    currentVariant?.tagSlugs?.includes("black-week") ?? false;
  const isBlackFridayTheme = isBlackFriday && isBlackWeekProduct;

  const shouldShowPriceDropBadge =
    hasSeniorityDiscount ||
    isBlackFridayTheme ||
    currentVariantSkuHasPromotion ||
    hasUpfrontPriceDrop;

  return (
    <>
      {shouldShowPriceDropBadge && (
        <div className="flex relative justify-center mt-8 -mb-3 w-full lg:justify-start">
          {(hasSeniorityDiscount && (
            <PriceDropBadge
              className={`px-[12px] py-[1px] bg-gradient-to-r from-neon-blue to-sky-magenta`}
            >
              {i18n.t(
                "catalog.badge.productFreeUpfront",
                {},
                {
                  html: true,
                },
              )}
            </PriceDropBadge>
          )) ||
            (isBlackFridayTheme && (
              <PriceDropBadge className={`px-[12px] py-[1px] bg-christmas`}>
                <Image
                  src="/images/christmas/gift.svg"
                  width={16}
                  height={16}
                />
                <p className="font-bold text-transparent uppercase bg-clip-text bg-white">
                  {i18n.t(
                    "catalog.badge.blackFridayPriceDrop",
                    {},
                    { html: true },
                  )}
                </p>
              </PriceDropBadge>
            )) ||
            ((currentVariantSkuHasPromotion || hasUpfrontPriceDrop) && (
              <PriceDropBadge
                className={`px-[12px] py-[1px] bg-cornflower-blue`}
              >
                {i18n.t("catalog.badge.priceDrop", {}, { html: true })}
                <DropArrow />
              </PriceDropBadge>
            ))}
        </div>
      )}

      <h2 className="flex flex-col justify-center">
        <br className="2xs:hidden" />
        <p className="2xs:mt-5">{subscriptionPriceLabel}</p>
      </h2>
      <p className="-mt-3 mb-3">{upfrontPriceLabel}</p>
    </>
  );
};
